import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, Row, Col, Card, Badge, Button, Table, 
  Modal, Form, Alert, Spinner, Tabs, Tab, ProgressBar
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar, faClock, faDollarSign, faUsers,
  faCheck, faTimes, faEye, faVideo, faHistory,
  faChartLine, faWallet, faUserMd, faCoins, faCheckDouble, faComments
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';
import Footer from '../components/footer';
import TherapyChat from '../components/TherapyChat';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import '../components/TherapyNew.css';

// Helper function to safely parse dates for Safari compatibility
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Replace space with 'T' for ISO format compatibility
  const isoString = dateString.replace(' ', 'T');
  
  try {
    return new Date(isoString);
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

const TherapistDashboard = () => {
  const { user } = useContext(MyContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [togglingAvailability, setTogglingAvailability] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Edit Profile states
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    bio: '',
    price_per_session: 0,
    photo_url: ''
  });
  const [editForm, setEditForm] = useState({
    bio: '',
    price_per_session: 0,
    photoFile: null,
    photoPreview: null
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Filter sessions by status
  const pendingSessions = sessions.filter(s => s.status === 'pending' || s.status === 'hold');
  const activeSessions = sessions.filter(s => s.status === 'active');
  const completedSessions = sessions.filter(s => s.status === 'completed' || s.status === 'closed');

  useEffect(() => {
    if (user) {
      loadTherapistData();
      loadProfileData(); // Load profile data on component mount
    }
  }, [user]);

  const loadTherapistData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load therapist sessions
      try {
      const sessionsResponse = await fetch(`${backendUrl}/fetch_therapist_sessions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ therapist_id: user.user_id })
      });
        
        // Log response for debugging
        const responseText = await sessionsResponse.text();
        console.log('Original API response:', responseText);
        
        try {
          const sessionsData = JSON.parse(responseText);
          console.log('Original API parsed:', sessionsData);

      if (sessionsData.success) {
        setSessions(sessionsData.sessions);
          } else {
            console.warn('Failed to load sessions:', sessionsData.messages);
          }
        } catch (jsonErr) {
          console.error('Failed to parse original API response:', jsonErr);
        }
      } catch (err) {
        console.error('Error loading sessions:', err);
      }

      // Load earnings (sessions array, compute summary)
      try {
        const earningsResponse = await fetch(`${backendUrl}/fetch_therapist_earnings.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ therapist_id: user.user_id })
        });
        const responseJson = await earningsResponse.json();
        console.log('Earnings API raw response:', responseJson);
        // If API returns earnings object, use it directly
        if (responseJson && responseJson.success && responseJson.earnings) {
          const e = responseJson.earnings;
          setEarnings({
            total_earnings: parseFloat(e.total_earnings) || 0,
            total_platform_fee: parseFloat(e.total_platform_fee) || 0,
            monthly_earnings: parseFloat(e.monthly_earnings) || 0,
            weekly_earnings: parseFloat(e.weekly_earnings) || 0,
            total_sessions: parseInt(e.total_sessions) || 0,
            completed_sessions: parseInt(e.completed_sessions) || 0,
            average_per_session: parseFloat(e.average_per_session) || 0
          });
        } else {
          setEarnings(null);
          if (responseJson && responseJson.messages) {
            console.warn('Failed to load earnings:', responseJson.messages);
          }
        }
      } catch (err) {
        console.error('Error loading earnings:', err);
      }

      // Load current availability status from users table
      try {
        const availabilityResponse = await fetch(`${backendUrl}/fetch_therapist_availability.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ therapist_id: user.user_id })
        });
        
        // Log response as text first for debugging
        const responseText = await availabilityResponse.json();
        console.log('Availability API raw response:', responseText);
        console.log('Response status:', availabilityResponse.status);
        console.log('Response headers:', Object.fromEntries(availabilityResponse.headers.entries()));
        
        if (!availabilityResponse.ok) {
          console.warn('Availability API returned non-OK status:', availabilityResponse.status);
          setIsAvailable(true); // Default to available
        } else {
          try {
            const availabilityData = JSON.parse(responseText);
            console.log('Availability parsed response:', availabilityData);
            
            if (availabilityData.success && availabilityData.is_available !== undefined) {
              setIsAvailable(availabilityData.is_available === 1);
            } else {
              // Default to available if not set or API error
              setIsAvailable(true);
            }
          } catch (jsonErr) {
            console.error('Failed to parse availability response as JSON:', jsonErr);
            console.error('Raw response was:', responseText);
            setIsAvailable(true); // Default to available on JSON parse error
          }
        }
      } catch (err) {
        console.error('Error loading availability:', err);
        setIsAvailable(true); // Default to available on error
      }

    } catch (err) {
      setError('Failed to load dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSessionStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'active': 'success',
      'completed': 'info',
      'declined': 'danger',
      'cancelled': 'danger',
      'closed': 'secondary',
      'paid': 'success'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatCoins = (coins) => {
    return `${coins?.toLocaleString() || '0'} coins`;
  };



  const formatPriceDisplay = (price) => {
    if (price === 0 || price === '0') {
      return <span className="text-success fw-bold">Free</span>;
    }
    return <span>{formatCurrency(price)}</span>;
  };

  const handleOpenChat = (session) => {
    setSelectedSession(session);
    setShowChatModal(true);
  };

  const handleCloseChat = () => {
    setShowChatModal(false);
    setSelectedSession(null);
  };

  // Helper function to normalize photo URL to relative path
  const normalizePhotoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) {
      // Extract relative path from full URL
      const urlParts = url.split('/api/schat/');
      return urlParts.length > 1 ? urlParts[1] : url;
    }
    return url;
  };

  // Edit Profile Functions
  const loadProfileData = async () => {
    try {
      const response = await fetch(`${backendUrl}/fetch_therapist_profile.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Normalize the photo URL to ensure it's always a relative path
        const normalizedProfile = {
          ...data.profile,
          photo_url: normalizePhotoUrl(data.profile.photo_url)
        };
        
        setProfileData(normalizedProfile);
        setEditForm({
          bio: normalizedProfile.bio || '',
          price_per_session: normalizedProfile.price_per_session || 0,
          photoFile: null,
          photoPreview: normalizedProfile.photo_url ? `${backendUrl}/${normalizedProfile.photo_url}` : null
        });
      } else {
        setProfileError(data.error || 'Failed to load profile data');
      }
    } catch (err) {
      setProfileError('Failed to load profile data: ' + err.message);
    }
  };

  const handleEditProfile = () => {
    setProfileError(null);
    setProfileSuccess(false);
    loadProfileData();
    setShowEditProfileModal(true);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setProfileError('Invalid file type. Please select a JPEG, PNG, or GIF image.');
        return;
      }
      
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        setProfileError('File size too large. Please select an image smaller than 2MB.');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditForm(prev => ({
          ...prev,
          photoFile: file,
          photoPreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
      setProfileError(null);
    }
  };

  const uploadProfilePhoto = async () => {
    if (!editForm.photoFile) {
      return null;
    }
    
    const formData = new FormData();
    formData.append('photo', editForm.photoFile);
    formData.append('user_id', user.user_id);
    
    try {
      const response = await fetch(`${backendUrl}/upload_therapist_photo.php`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        return data.photo_url;
      } else {
        throw new Error(data.error || 'Failed to upload photo');
      }
    } catch (err) {
      throw new Error('Failed to upload photo: ' + err.message);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setUpdatingProfile(true);
      setProfileError(null);
      
      // Upload photo first if selected
      let photoUrl = '';
      if (editForm.photoFile) {
        // New photo uploaded

        photoUrl = await uploadProfilePhoto();

      } else if (profileData.photo_url) {
        // Keep existing photo URL
        photoUrl = profileData.photo_url;
      }
         
      
      const response = await fetch(`${backendUrl}/update_therapist_profile.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          bio: editForm.bio,
          photo_url: photoUrl,
          price_per_session: parseFloat(editForm.price_per_session)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setProfileSuccess(true);
        setProfileData(prev => ({
          ...prev,
          bio: editForm.bio,
          price_per_session: editForm.price_per_session,
          photo_url: photoUrl
        }));
        setTimeout(() => {
          setShowEditProfileModal(false);
          setProfileSuccess(false);
        }, 2000);
      } else {
        setProfileError(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setProfileError('Failed to update profile: ' + err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  const toggleAvailability = async () => {
    try {
      setTogglingAvailability(true);
      const newAvailability = !isAvailable;
      
      const response = await fetch(`${backendUrl}/toggle_therapy_availability.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          therapist_id: user.user_id,
          is_available: newAvailability
        })
      });
      
      // Log response as text first for debugging
      const responseText = await response.text();
      console.log('Toggle availability raw response:', responseText);
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      try {
        const data = JSON.parse(responseText);
        console.log('Toggle availability parsed response:', data);
        
        if (data.success) {
          setIsAvailable(newAvailability);
          setError(null);
        } else {
          setError(data.messages || 'Failed to update availability');
        }
      } catch (jsonErr) {
        console.error('Failed to parse toggle response as JSON:', jsonErr);
        console.error('Raw response was:', responseText);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Error toggling availability:', err);
      setError('Failed to update availability: ' + err.message);
    } finally {
      setTogglingAvailability(false);
    }
  };

  const handleSessionAction = async (sessionId, action, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      setActionLoading(true);
      
      let endpoint = '';
      if (action === 'approve') {
        endpoint = 'approve_therapy_session_simple.php';
      } else if (action === 'decline') {
        endpoint = 'approve_therapy_session_simple.php'; // Same endpoint, different action
      } else if (action === 'complete') {
        endpoint = 'complete_therapy_session.php';
      }
      
      const requestData = {
        session_id: sessionId,
        therapist_id: user.user_id,
        action: action
      };
      
      console.log('Sending session action request:', requestData);
      console.log('User ID:', user.user_id);
      console.log('Session ID:', sessionId);
      console.log('Endpoint:', endpoint);
      
      const response = await fetch(`${backendUrl}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      const responseText = await response.text();
      console.log('Session action raw response:', responseText);
      
      try {
        const data = JSON.parse(responseText);
        console.log('Session action parsed response:', data);
        
        if (data.success) {
          console.log('Session action successful:', data);
          setError(null);
          setShowSessionModal(false);
          await loadTherapistData(); // Refresh data
          console.log('Data refreshed after session action');
        } else {
          setError(data.messages || `Failed to ${action} session`);
        }
      } catch (jsonErr) {
        console.error('Failed to parse session action response as JSON:', jsonErr);
        console.error('Raw response was:', responseText);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Error handling session action:', err);
      setError(`Failed to ${action} session: ` + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      <Container fluid className="py-4" style={{ marginBottom: '100px' }}>
        <Row className="mb-4">
          <Col>
            <h2>
              <FontAwesomeIcon icon={faUserMd} className="me-2 text-primary" />
              Therapist Dashboard
            </h2>
            <p className="text-muted">Manage your sessions and view earnings</p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faClock} size="2x" className="text-warning mb-2" />
                <h4>{pendingSessions.length}</h4>
                <p className="text-muted">Pending Requests</p>
            
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCalendar} size="2x" className="text-success mb-2" />
                <h4>{activeSessions.length}</h4>
                <p className="text-muted">Scheduled Sessions</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCheck} size="2x" className="text-info mb-2" />
                <h4>{completedSessions.length}</h4>
                <p className="text-muted">Completed Sessions</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCoins} size="2x" className="text-success mb-2" />
                <h4>{formatCoins(earnings?.total_earnings || 0)}</h4>
                <p className="text-muted">Total Earnings</p>
   
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Availability Toggle */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="me-3">
                    <h5 className="mb-1">Session Availability</h5>
                    <p className="text-muted mb-0">
                      {isAvailable ? 'You are currently accepting new session requests' : 'You are currently not accepting new session requests'}
                    </p>
                  </div>
                  <div className="ms-3">
                    <Button
                      variant={isAvailable ? 'success' : 'secondary'}
                      size="lg"
                      onClick={toggleAvailability}
                      disabled={togglingAvailability}
                      className="px-4"
                    >
                      {togglingAvailability ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <FontAwesomeIcon 
                            icon={isAvailable ? faCheck : faTimes} 
                            className="me-2" 
                          />
                          {isAvailable ? 'Available' : 'Unavailable'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          <Tab eventKey="overview" title="Overview">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Pending Session Requests</h5>
                  </Card.Header>
                  <Card.Body>
                    {pendingSessions.length === 0 ? (
                      <p className="text-muted">No pending session requests</p>
                    ) : (
                      <div>
                        {pendingSessions.slice(0, 5).map((session) => (
                          <div key={session.session_id} className="border-bottom pb-2 mb-2">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>{session.client_name}</strong>
                                <br />
                                <small className="text-muted">{session.issue_text}</small>
                              </div>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowSessionModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Earnings Summary</h5>
                  </Card.Header>
                  <Card.Body>
                    {earnings ? (
                      <div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>This Month:</span>
                          <strong>{formatCoins(earnings.monthly_earnings || 0)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>This Week:</span>
                          <strong>{formatCoins(earnings.weekly_earnings || 0)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Total Sessions:</span>
                          <strong>{earnings.total_sessions || 0}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Average per Session:</span>
                          <strong>{formatCoins(earnings.average_per_session || 0)}</strong>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted">Loading earnings data...</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="sessions" title="All Sessions">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Session Management</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Issue</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions?.length > 0 ? (
                      sessions.map((session) => (
                      <tr key={session.session_id}>
                        <td>{session.client_name}</td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '200px' }}>
                            {session.issue_text}
                          </div>
                        </td>
                        <td>{getSessionStatusBadge(session.status)}</td>
                        <td>{parseDate(session.created_at)?.toLocaleDateString() || 'Invalid date'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowSessionModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              View
                            </Button>
                            {(session.status === 'active' || session.status === 'closed') && (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleOpenChat(session)}
                              >
                                <FontAwesomeIcon icon={faComments} className="me-1" />
                                Chat
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          {loading ? 'Loading sessions...' : 'No sessions found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="pending" title={
            <span>
              Pending Requests
              {pendingSessions.length > 0 && (
                <Badge bg="warning" text="dark" pill className="ms-2">
                  {pendingSessions.length}
                </Badge>
              )}
            </span>
          }>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Pending Session Requests</h5>
              </Card.Header>
              <Card.Body>
                {pendingSessions.length === 0 ? (
                  <p className="text-muted">No pending session requests</p>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Issue</th>
                        <th>Status</th>
                        <th>Requested</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingSessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>{session.client_name}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{getSessionStatusBadge(session.status)}</td>
                          <td>{parseDate(session.created_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowSessionModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="active" title={
            <span>
              Active Sessions
              {activeSessions.length > 0 && (
                <Badge bg="success" pill className="ms-2">
                  {activeSessions.length}
                </Badge>
              )}
            </span>
          }>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Active Sessions</h5>
              </Card.Header>
              <Card.Body>
                {activeSessions.length === 0 ? (
                  <p className="text-muted">No active sessions</p>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Issue</th>
                        <th>Started</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>{session.client_name}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{parseDate(session.updated_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleOpenChat(session)}
                              >
                                <FontAwesomeIcon icon={faComments} className="me-1" />
                                Open Chat
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowSessionModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="completed" title={
            <span>
              Completed
              {completedSessions.length > 0 && (
                <Badge bg="info" pill className="ms-2">
                  {completedSessions.length}
                </Badge>
              )}
            </span>
          }>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Completed & Closed Sessions</h5>
              </Card.Header>
              <Card.Body>
                {completedSessions.length === 0 ? (
                  <p className="text-muted">No completed sessions</p>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Issue</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedSessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>{session.client_name}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{getSessionStatusBadge(session.status)}</td>
                          <td>{parseDate(session.updated_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleOpenChat(session)}
                              >
                                <FontAwesomeIcon icon={faComments} className="me-1" />
                                View Chat
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowSessionModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="earnings" title="Earnings">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Earnings Details</h5>
              </Card.Header>
              <Card.Body>
                {earnings ? (
                  <Row>
                    <Col md={6}>
                      <h6>Earnings Breakdown</h6>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Total Earnings:</span>
                          <strong>{formatCoins(earnings.total_earnings || 0)}</strong>
                        </div>
                        <ProgressBar 
                          now={100} 
                          variant="success" 
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>This Month:</span>
                          <strong>{formatCoins(earnings.monthly_earnings || 0)}</strong>
                        </div>
                        <ProgressBar 
                          now={earnings.total_earnings > 0 ? (earnings.monthly_earnings / earnings.total_earnings) * 100 : 0} 
                          variant="info" 
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>This Week:</span>
                          <strong>{formatCoins(earnings.weekly_earnings || 0)}</strong>
                        </div>
                        <ProgressBar 
                          now={earnings.total_earnings > 0 ? (earnings.weekly_earnings / earnings.total_earnings) * 100 : 0} 
                          variant="warning" 
                          className="mt-1"
                        />
                      </div>
                    </Col>
                    
                    <Col md={6}>
                      <h6>Session Statistics</h6>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Total Sessions:</span>
                          <strong>{earnings.total_sessions || 0}</strong>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Completed Sessions:</span>
                          <strong>{earnings.completed_sessions || 0}</strong>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Average per Session:</span>
                          <strong>{formatCoins(earnings.average_per_session || 0)}</strong>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>Platform Fee (20%):</span>
                          <strong>{formatCoins(earnings.total_platform_fee || 0)}</strong>
                        </div>
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <p className="text-muted">Loading earnings data...</p>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="profile" title="Edit Profile">
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Profile Information</h5>
                  <Button variant="primary" onClick={handleEditProfile}>
                    <FontAwesomeIcon icon={faUserMd} className="me-2" />
                    Edit Profile
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="text-center mb-3">
                      {profileData.photo_url ? (
                        <img 
                          src={`${backendUrl}/${profileData.photo_url}`} 
                          alt="Profile" 
                          className="img-fluid rounded-circle"
                          style={{ width: '150px', height: '150px', objectFit: 'cover', margin: '0 auto' }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mx-auto"
                          style={{ width: '150px', height: '150px' }}
                        >
                          <FontAwesomeIcon icon={faUserMd} size="3x" className="text-white" />
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={8}>
                    <h6>Current Profile</h6>
                    <div className="mb-3">
                      <strong>Name:</strong>
                      <br />
                      <span>{profileData.display_name || 'N/A'}</span>
                    </div>
                    <div className="mb-3">
                      <strong>Price per Session:</strong>
                      <br />
                      {formatPriceDisplay(profileData.price_per_session)}
                    </div>
                    <div className="mb-3">
                      <strong>Status:</strong>
                      <br />
                      <Badge bg={profileData.application_status === 'approved' ? 'success' : 'warning'}>
                        {profileData.application_status || 'N/A'}
                      </Badge>
                    </div>
                    <div className="mb-3">
                      <strong>Total Sessions:</strong>
                      <br />
                      <span>{profileData.total_sessions || 0}</span>
                    </div>
                    <div className="mb-3">
                      <strong>Rating:</strong>
                      <br />
                      <span>{profileData.rating || 0}/5 ⭐</span>
                    </div>
                    <div className="mb-3">
                      <strong>Categories:</strong>
                      <br />
                      {profileData.categories && profileData.categories.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {profileData.categories.map((cat, index) => (
                            <Badge key={index} bg="info" className="me-1">
                              {cat.category_name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No categories assigned</span>
                      )}
                    </div>
                    <div className="mb-3">
                      <strong>Bio:</strong>
                      <br />
                      <p className="text-muted">
                        {profileData.bio || 'No bio provided'}
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      {/* Session Detail Modal */}
      <Modal show={showSessionModal} onHide={() => setShowSessionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Session Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSession && (
            <div>
              <Row>
                <Col md={6}>
                  <h6>Session Information</h6>
                  <p><strong>Requested:</strong> {parseDate(selectedSession.created_at)?.toLocaleString() || 'Invalid date'}</p>
                  {selectedSession.scheduled_time && (
                    <p><strong>Scheduled:</strong> {parseDate(selectedSession.scheduled_time)?.toLocaleString() || 'Invalid date'}</p>
                  )}
                  <p>
                    <strong>Status:</strong> {getSessionStatusBadge(selectedSession.status)}
                  </p>
                </Col>
                <Col md={6}>
                  <h6>Payment Details</h6>
                  <p><strong>Amount:</strong> {formatCoins(selectedSession.coins_paid || 0)}</p>
                </Col>
              </Row>
              
              <hr />
              
              <h6>Client's Issue</h6>
              <p>{selectedSession.issue_text}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSessionModal(false)}>
            Close
          </Button>
          {selectedSession?.status === 'pending' && (
            <>
              <Button 
                variant="danger"
                onClick={(e) => handleSessionAction(selectedSession.session_id, 'decline', e)}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : (
                  <>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Decline & Refund
                  </>
                )}
              </Button>
              <Button 
                variant="success"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Approve button clicked');
                  console.log('Session data:', selectedSession);
                  handleSessionAction(selectedSession.session_id, 'approve', e);
                }}
                disabled={actionLoading}
              >
                {actionLoading ? <Spinner animation="border" size="sm" /> : (
                  <>
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Approve Session
                  </>
                )}
              </Button>
            </>
          )}
          
          {selectedSession?.status === 'active' && (
            <>
              <Button 
                variant="info"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Start chat clicked');
                  setShowSessionModal(false);
                  handleOpenChat(selectedSession);
                }}
              >
                <FontAwesomeIcon icon={faComments} className="me-1" />
                Start Chat
              </Button>
              <Button 
                variant="primary"
                onClick={(e) => handleSessionAction(selectedSession.session_id, 'complete', e)}
                disabled={actionLoading}
              >
                { (actionLoading && 3 > 4) ? <Spinner animation="border" size="sm" /> : (
                  <>
                    <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
                    Complete Session & Receive Payment
                  </>
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Chat Modal */}
      <Modal 
        show={showChatModal} 
        onHide={handleCloseChat} 
        size="xl"
        dialogClassName="chat-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faComments} className="me-2" />
            Therapy Session Chat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedSession && (
            <TherapyChat
              session={selectedSession}
              userId={user.user_id}
              onClose={handleCloseChat}
              isTherapist={true}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal show={showEditProfileModal} onHide={() => setShowEditProfileModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faUserMd} className="me-2" />
            Edit Profile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {profileSuccess && (
            <Alert variant="success" onClose={() => setProfileSuccess(false)} dismissible>
              Profile updated successfully!
            </Alert>
          )}
          
          {profileError && (
            <Alert variant="danger" onClose={() => setProfileError(null)} dismissible>
              {profileError}
            </Alert>
          )}

          <Form onSubmit={handleProfileSubmit}>
            <Row>
              <Col md={4}>
                <div className="text-center mb-3">
                  {editForm.photoPreview ? (
                    <img 
                      src={editForm.photoPreview} 
                      alt="Profile Preview" 
                      className="img-fluid rounded-circle"
                      style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div 
                      className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mx-auto"
                      style={{ width: '150px', height: '150px' }}
                    >
                      <FontAwesomeIcon icon={faUserMd} size="3x" className="text-white" />
                    </div>
                  )}
                  <div className="mt-2">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="form-control-sm"
                    />
                    <small className="text-muted">Max 2MB, JPEG, PNG, or GIF</small>
                  </div>
                </div>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Price per Session</strong></Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.price_per_session}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      price_per_session: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Enter price (0 for free sessions)"
                  />
                  <Form.Text className="text-muted">
                    Set to 0 for free sessions
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Bio</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editForm.bio}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      bio: e.target.value
                    }))}
                    placeholder="Tell clients about your experience, approach, and what you can help with..."
                  />
                  <Form.Text className="text-muted">
                    This will be visible to potential clients
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditProfileModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleProfileSubmit}
            disabled={updatingProfile}
          >
            {updatingProfile ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Update Profile'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  );
};

export default TherapistDashboard; 